import { useState, useMemo } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { AlertTriangle, CloudRain, Sun, Cloud, Snowflake, ChevronDown, ChevronUp, Users, RefreshCw, ArrowRightLeft, Clock, CalendarRange, CheckSquare, X } from "lucide-react";
import { format, addDays, startOfDay, eachDayOfInterval, parse } from "date-fns";
import { fr } from "date-fns/locale";
import { supabase } from "@/integrations/supabase/client";
import { formatCustomerName } from "@/lib/clientUtils";
import { escapeHtml } from "@/lib/htmlEscape";
import { useWeatherForecast, type WeatherForecastDay } from "@/hooks/useWeatherForecast";
import { useTerraceClosures } from "@/hooks/useTerraceClosures";
import { TerraceClosureImpactDialog } from "@/components/TerraceClosureImpactDialog";
import { isReservationOnTerrace } from "@/lib/terraceReservationUtils";
import { useReservationSettings } from "@/hooks/useReservationSettings";
import { formatDateLocal } from "@/lib/timeFormatUtils";
import { toast } from "@/hooks/use-toast";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Table, CombinedTable, Reservation, TableAvailability, TableWeekdayAvailability, TableBookablePeriod } from "@/types/table";
import { TimeSlot } from "@/lib/timeSlotUtils";
import { findOptimalTable, findAllAvailableIndoorTables, getOptimalityScore } from "@/lib/tableAssignmentUtils";
import { ConflictCheckOptions } from "@/lib/reservationConflictUtils";
import { isPhysicalOverlapError, getConflictToastMessage } from "@/lib/reservationConflictError";
import { useEstablishment } from "@/contexts/EstablishmentContext";
import { cn } from "@/lib/utils";

interface TerraceManagementTabProps {
  establishmentId: string;
  reservations: Reservation[];
  tables: Table[];
  combinedTables: CombinedTable[];
  timeSlots: TimeSlot[];
  availabilities: TableAvailability[];
  weekdayAvailabilities: TableWeekdayAvailability[];
  bookablePeriods: TableBookablePeriod[];
  childrenCapacityMode: 'full' | 'half' | 'none';
}

interface IndoorProposal {
  reservation: Reservation;
  proposedTable: { type: 'single' | 'combined'; id: string; capacity: number; name: string } | null;
  allAvailableTables: Array<{ type: 'single' | 'combined'; id: string; capacity: number; name: string }>;
  selectedTable: { type: 'single' | 'combined'; id: string; capacity: number; name: string } | null;
}

type EmailAction = {
  type: 'weather_confirmation' | 'weather_cancellation';
  reservation: Reservation;
  tableName?: string;
} | null;

function getWeatherIcon(category: string) {
  switch (category) {
    case 'sunny': return <Sun className="w-5 h-5 text-amber-500" />;
    case 'cloudy': return <Cloud className="w-5 h-5 text-muted-foreground" />;
    case 'rainy': return <CloudRain className="w-5 h-5 text-blue-500" />;
    case 'snowy': return <Snowflake className="w-5 h-5 text-blue-300" />;
    default: return <Cloud className="w-5 h-5 text-muted-foreground" />;
  }
}

export function TerraceManagementTab({ establishmentId, reservations, tables, combinedTables, timeSlots, availabilities, weekdayAvailabilities, bookablePeriods, childrenCapacityMode }: TerraceManagementTabProps) {
  const { forecast, loading: weatherLoading, refetch: refetchWeather } = useWeatherForecast();
  const { closures, loading: closuresLoading, addClosure, removeClosure, removeClosuresByDate, getClosuresForDate, isTerraceClosedOnDate } = useTerraceClosures({ establishmentId });
  const { settings } = useReservationSettings({ establishmentId });
  const { currentEstablishment } = useEstablishment();
  
  const [expandedDate, setExpandedDate] = useState<string | null>(null);
  const [impactDialogOpen, setImpactDialogOpen] = useState(false);
  const [closingDate, setClosingDate] = useState<string | null>(null);
  const [closingTimeSlotId, setClosingTimeSlotId] = useState<string | null>(null);
  const [indoorProposal, setIndoorProposal] = useState<IndoorProposal | null>(null);
  const [reassigning, setReassigning] = useState(false);
  const [emailAction, setEmailAction] = useState<EmailAction>(null);
  const [sendingEmail, setSendingEmail] = useState(false);
  // Track in-session reassignments so successive proposals within the same
  // Terrace tab session don't propose (or offer) a table already taken.
  const [sessionAssignments, setSessionAssignments] = useState<
    Map<string, { tableId: string | null; combinedTableId: string | null }>
  >(new Map());

  const applySessionOverrides = (list: Reservation[]): Reservation[] =>
    list.map(other => {
      const override = sessionAssignments.get(other.supabaseId || other.id);
      if (!override) return other;
      return { ...other, tableId: override.tableId, combinedTableId: override.combinedTableId };
    });

  // Bulk mode state
  const [bulkMode, setBulkMode] = useState(false);
  const [selectedDates, setSelectedDates] = useState<Set<string>>(new Set());
  const [bulkClosing, setBulkClosing] = useState(false);
  const [bulkOpening, setBulkOpening] = useState(false);

  // Period closure dialog state
  const [periodDialogOpen, setPeriodDialogOpen] = useState(false);
  const [periodStart, setPeriodStart] = useState<Date | undefined>();
  const [periodEnd, setPeriodEnd] = useState<Date | undefined>();
  const [periodClosing, setPeriodClosing] = useState(false);

  const maxBookingDays = settings?.maxBookingDays ?? 30;
  const durationMinutes = settings?.reservationDurationMinutes ?? 120;

  // Generate date range
  const dateRange = useMemo(() => {
    const dates: string[] = [];
    const today = startOfDay(new Date());
    for (let i = 0; i < maxBookingDays; i++) {
      dates.push(formatDateLocal(addDays(today, i)));
    }
    return dates;
  }, [maxBookingDays]);

  // Weather lookup
  const weatherByDate = useMemo(() => {
    const map = new Map<string, WeatherForecastDay>();
    forecast.forEach(f => map.set(f.date, f));
    return map;
  }, [forecast]);

  // Build terrace table ID sets for fast lookup
  const terraceTableIds = useMemo(() => new Set(tables.filter(t => t.isTerrace).map(t => t.id)), [tables]);
  const terraceCombinedTableIds = useMemo(() => new Set(combinedTables.filter(ct => ct.isTerrace).map(ct => ct.id)), [combinedTables]);

  // Terrace reservations by date: preference OR assigned to terrace table
  const terraceReservationsByDate = useMemo(() => {
    const map = new Map<string, typeof reservations>();
    reservations.forEach(r => {
      if (isReservationOnTerrace(r, terraceTableIds, terraceCombinedTableIds)) {
        const dateStr = formatDateLocal(r.date);
        const existing = map.get(dateStr) || [];
        existing.push(r);
        map.set(dateStr, existing);
      }
    });
    return map;
  }, [reservations, terraceTableIds, terraceCombinedTableIds]);

  const getClosureStatus = (dateStr: string) => {
    const dateCl = getClosuresForDate(dateStr);
    if (dateCl.length === 0) return 'open';
    if (dateCl.some(c => c.timeSlotId === null)) return 'closed';
    return 'partial';
  };

  const handleCloseTerrace = (dateStr: string, timeSlotId?: string | null) => {
    const terraceResCount = terraceReservationsByDate.get(dateStr)?.length || 0;
    if (terraceResCount > 0) {
      setClosingDate(dateStr);
      setClosingTimeSlotId(timeSlotId ?? null);
      setImpactDialogOpen(true);
    } else {
      confirmClosure(dateStr, timeSlotId ?? null);
    }
  };

  const confirmClosure = async (dateStr: string, timeSlotId: string | null) => {
    try {
      const weather = weatherByDate.get(dateStr);
      const reason = weather?.suggestion || 'Fermeture manuelle';
      await addClosure.mutateAsync({ date: dateStr, timeSlotId, reason });
      toast({ title: "Terrasse fermée", description: `Terrasse fermée pour le ${format(new Date(dateStr), 'd MMMM', { locale: fr })}` });
    } catch {
      toast({ title: "Erreur", description: "Impossible de fermer la terrasse.", variant: "destructive" });
    }
  };

  const handleOpenTerrace = async (dateStr: string) => {
    try {
      await removeClosuresByDate.mutateAsync(dateStr);
      toast({ title: "Terrasse ouverte", description: `Terrasse rouverte pour le ${format(new Date(dateStr), 'd MMMM', { locale: fr })}` });
    } catch {
      toast({ title: "Erreur", description: "Impossible de rouvrir la terrasse.", variant: "destructive" });
    }
  };

  const handleRemoveSingleClosure = async (closureId: string, dateStr: string, timeSlotId: string | null) => {
    try {
      await removeClosure.mutateAsync(closureId);
      const ts = timeSlotId ? timeSlots.find(t => t.id === timeSlotId) : null;
      const slotLabel = ts ? `${ts.start} - ${ts.end}` : 'la journée';
      toast({ title: "Créneau rouvert", description: `Terrasse rouverte pour ${slotLabel} le ${format(new Date(dateStr), 'd MMMM', { locale: fr })}` });
    } catch {
      toast({ title: "Erreur", description: "Impossible de rouvrir ce créneau.", variant: "destructive" });
    }
  };

  // Bulk mode helpers
  const toggleBulkMode = () => {
    if (bulkMode) {
      setBulkMode(false);
      setSelectedDates(new Set());
    } else {
      setBulkMode(true);
    }
  };

  const toggleDateSelection = (dateStr: string) => {
    setSelectedDates(prev => {
      const next = new Set(prev);
      if (next.has(dateStr)) {
        next.delete(dateStr);
      } else {
        next.add(dateStr);
      }
      return next;
    });
  };

  const handleBulkClose = async () => {
    if (selectedDates.size === 0) return;
    setBulkClosing(true);
    let successCount = 0;
    try {
      for (const dateStr of selectedDates) {
        // Skip already closed dates
        if (getClosureStatus(dateStr) === 'closed') continue;
        const weather = weatherByDate.get(dateStr);
        const reason = weather?.suggestion || 'Fermeture manuelle (lot)';
        await addClosure.mutateAsync({ date: dateStr, timeSlotId: null, reason });
        successCount++;
      }
      toast({ title: "Terrasse fermée", description: `${successCount} jour${successCount > 1 ? 's' : ''} fermé${successCount > 1 ? 's' : ''}` });
      setBulkMode(false);
      setSelectedDates(new Set());
    } catch {
      toast({ title: "Erreur", description: "Erreur lors de la fermeture en lot.", variant: "destructive" });
    } finally {
      setBulkClosing(false);
    }
  };

  const handleBulkOpen = async () => {
    if (selectedDates.size === 0) return;
    setBulkOpening(true);
    let successCount = 0;
    try {
      for (const dateStr of selectedDates) {
        const st = getClosureStatus(dateStr);
        if (st === 'open') continue;
        await removeClosuresByDate.mutateAsync(dateStr);
        successCount++;
      }
      toast({ title: "Terrasse rouverte", description: `${successCount} jour${successCount > 1 ? 's' : ''} rouvert${successCount > 1 ? 's' : ''}` });
      setBulkMode(false);
      setSelectedDates(new Set());
    } catch {
      toast({ title: "Erreur", description: "Erreur lors de la réouverture en lot.", variant: "destructive" });
    } finally {
      setBulkOpening(false);
    }
  };

  // Period closure
  const handlePeriodClose = async () => {
    if (!periodStart || !periodEnd) return;
    setPeriodClosing(true);
    try {
      const days = eachDayOfInterval({ start: periodStart, end: periodEnd });
      let successCount = 0;
      for (const day of days) {
        const dateStr = formatDateLocal(day);
        if (getClosureStatus(dateStr) === 'closed') continue;
        const weather = weatherByDate.get(dateStr);
        const reason = weather?.suggestion || 'Fermeture période';
        await addClosure.mutateAsync({ date: dateStr, timeSlotId: null, reason });
        successCount++;
      }
      toast({ title: "Période fermée", description: `${successCount} jour${successCount > 1 ? 's' : ''} fermé${successCount > 1 ? 's' : ''}` });
      setPeriodDialogOpen(false);
      setPeriodStart(undefined);
      setPeriodEnd(undefined);
    } catch {
      toast({ title: "Erreur", description: "Erreur lors de la fermeture de la période.", variant: "destructive" });
    } finally {
      setPeriodClosing(false);
    }
  };

  const handleIndoorClick = (reservation: Reservation) => {
    const conflictOptions: ConflictCheckOptions = {
      reservationDurationMinutes: reservation.durationMinutes ?? durationMinutes,
    };

    // Exclude the current reservation from conflict checks, and apply in-session
    // reassignments so a table already picked earlier is treated as occupied.
    const otherReservations = applySessionOverrides(
      reservations.filter(r => (r.supabaseId || r.id) !== (reservation.supabaseId || reservation.id))
    );

    const proposedTable = findOptimalTable(
      reservation.numberOfPeople,
      reservation.date,
      reservation.startTime,
      tables,
      combinedTables,
      otherReservations,
      availabilities,
      weekdayAvailabilities,
      conflictOptions,
      bookablePeriods,
      reservation.numberOfChildren ?? 0,
      childrenCapacityMode,
      reservation.hasStroller ?? false,
      'indoor',
      undefined,
      settings?.customAssignmentOrderEnabled ?? false
    );

    const allAvailableTables = findAllAvailableIndoorTables(
      reservation.numberOfPeople,
      reservation.date,
      reservation.startTime,
      tables,
      combinedTables,
      otherReservations,
      availabilities,
      weekdayAvailabilities,
      conflictOptions,
      bookablePeriods,
      reservation.numberOfChildren ?? 0,
      childrenCapacityMode,
      reservation.hasStroller ?? false,
    );

    setIndoorProposal({ reservation, proposedTable, allAvailableTables, selectedTable: proposedTable });
  };

  const handleConfirmIndoor = async () => {
    if (!indoorProposal) return;
    setReassigning(true);
    const { reservation, selectedTable } = indoorProposal;
    try {
      const updateData: Record<string, unknown> = { terrace_preference: 'indoor' };
      if (selectedTable) {
        if (selectedTable.type === 'single') {
          updateData.table_id = selectedTable.id;
          updateData.combined_table_id = null;
        } else {
          updateData.combined_table_id = selectedTable.id;
          updateData.table_id = null;
        }
      }
      const { error: updErr } = await supabase
        .from('reservations')
        .update(updateData)
        .eq('id', reservation.supabaseId || reservation.id);
      if (updErr) throw updErr;
      if (selectedTable) {
        setSessionAssignments(prev => {
          const next = new Map(prev);
          next.set(reservation.supabaseId || reservation.id, {
            tableId: selectedTable.type === 'single' ? selectedTable.id : null,
            combinedTableId: selectedTable.type === 'combined' ? selectedTable.id : null,
          });
          return next;
        });
      }
      toast({
        title: "Réassigné",
        description: selectedTable
          ? `${formatCustomerName(reservation.customerFirstName, reservation.customerLastName)} déplacé sur ${selectedTable.name}`
          : `${formatCustomerName(reservation.customerFirstName, reservation.customerLastName)} — préférence changée en intérieur`,
      });
      // Transition to email step
      setIndoorProposal(null);
      setEmailAction({
        type: 'weather_confirmation',
        reservation,
        
      });
    } catch (err) {
      if (isPhysicalOverlapError(err)) {
        const { title, description } = getConflictToastMessage();
        toast({ title, description, variant: "destructive" });
      } else {
        toast({ title: "Erreur", description: "Impossible de réassigner.", variant: "destructive" });
      }
    } finally {
      setReassigning(false);
    }
  };

  const handlePreferenceOnlyIndoor = async () => {
    if (!indoorProposal) return;
    setReassigning(true);
    const { reservation } = indoorProposal;
    try {
      await supabase.from('reservations').update({ terrace_preference: 'indoor' }).eq('id', reservation.supabaseId || reservation.id);
      toast({ title: "Préférence mise à jour", description: `${formatCustomerName(reservation.customerFirstName, reservation.customerLastName)} — préférence changée en intérieur` });
      setIndoorProposal(null);
      setEmailAction({
        type: 'weather_confirmation',
        reservation,
      });
    } catch {
      toast({ title: "Erreur", description: "Impossible de mettre à jour.", variant: "destructive" });
    } finally {
      setReassigning(false);
    }
  };

  const handleCancelAndNotify = async () => {
    if (!indoorProposal) return;
    setReassigning(true);
    const { reservation } = indoorProposal;
    try {
      await supabase.from('reservations').update({ status: 'annulé' }).eq('id', reservation.supabaseId || reservation.id);
      toast({ title: "Réservation annulée", description: formatCustomerName(reservation.customerFirstName, reservation.customerLastName) });
      setIndoorProposal(null);
      setEmailAction({
        type: 'weather_cancellation',
        reservation,
      });
    } catch {
      toast({ title: "Erreur", description: "Impossible d'annuler.", variant: "destructive" });
    } finally {
      setReassigning(false);
    }
  };

  const handleSendEmail = async () => {
    if (!emailAction) return;
    setSendingEmail(true);
    try {
      const emailType = emailAction.type === 'weather_confirmation' ? 'confirmation' : 'cancellation';
      const customBodyHtml = generateEmailPreviewHtml(emailAction);
      const estName = currentEstablishment?.name || 'Notre établissement';
      const customSubject = emailAction.type === 'weather_confirmation'
        ? `Confirmation de votre réservation — ${estName}`
        : `Annulation de votre réservation — ${estName}`;

      const { data, error } = await supabase.functions.invoke('send-manual-email', {
        body: {
          reservationId: emailAction.reservation.supabaseId || emailAction.reservation.id,
          emailType,
          customSubject,
          customBodyHtml,
        },
      });
      if (error) throw error;
      if (data && !data.success) throw new Error(data.error || 'Erreur inconnue');
      toast({ title: "Email envoyé", description: `Email de ${emailType === 'confirmation' ? 'confirmation' : 'annulation'} envoyé avec succès.` });
      setEmailAction(null);
    } catch {
      toast({ title: "Erreur", description: "Impossible d'envoyer l'email.", variant: "destructive" });
    } finally {
      setSendingEmail(false);
    }
  };

  const generateEmailPreviewHtml = (action: NonNullable<EmailAction>) => {
    const estName = escapeHtml(currentEstablishment?.name || 'Notre établissement');
    const clientName = escapeHtml(
      formatCustomerName(action.reservation.customerFirstName, action.reservation.customerLastName)
    );
    const dateStr = escapeHtml(format(new Date(action.reservation.date), 'd MMMM yyyy', { locale: fr }));
    const timeStr = escapeHtml(action.reservation.startTime.substring(0, 5));
    const people = action.reservation.numberOfPeople;

    if (action.type === 'weather_confirmation') {
      return `
        <div style="font-family: Arial, sans-serif; max-width: 500px; margin: 0 auto; padding: 24px;">
          <h2 style="color: #1a1a1a; font-size: 18px;">Confirmation de votre réservation</h2>
          <p>Bonjour ${clientName},</p>
          <p>Nous vous confirmons que votre réservation est maintenue :</p>
          <ul style="list-style: none; padding: 0;">
            <li style="padding: 4px 0;">📅 <strong>${dateStr}</strong> à <strong>${timeStr}</strong></li>
            <li style="padding: 4px 0;">👥 <strong>${people} personne${people > 1 ? 's' : ''}</strong></li>
            
          </ul>
          <p style="background: #f0f9ff; border-left: 3px solid #3b82f6; padding: 12px; border-radius: 4px;">
            ℹ️ En raison des conditions météorologiques, vous serez placé <strong>en intérieur</strong>.
          </p>
          <p>Nous nous réjouissons de vous accueillir !</p>
          <p style="color: #6b7280; font-size: 13px;">— ${estName}</p>
        </div>
      `;
    } else {
      return `
        <div style="font-family: Arial, sans-serif; max-width: 500px; margin: 0 auto; padding: 24px;">
          <h2 style="color: #1a1a1a; font-size: 18px;">Annulation de votre réservation</h2>
          <p>Bonjour ${clientName},</p>
          <p>Nous sommes au regret de vous informer que votre réservation a dû être annulée :</p>
          <ul style="list-style: none; padding: 0;">
            <li style="padding: 4px 0;">📅 <strong>${dateStr}</strong> à <strong>${timeStr}</strong></li>
            <li style="padding: 4px 0;">👥 <strong>${people} personne${people > 1 ? 's' : ''}</strong></li>
          </ul>
          <p style="background: #fef2f2; border-left: 3px solid #ef4444; padding: 12px; border-radius: 4px;">
            En raison des conditions météorologiques défavorables, nous n'avons malheureusement pas pu maintenir votre réservation en terrasse ni vous proposer une alternative en intérieur.
          </p>
          <p>Nous vous prions de nous excuser pour ce désagrément et espérons vous accueillir très prochainement.</p>
          <p style="color: #6b7280; font-size: 13px;">— ${estName}</p>
        </div>
      `;
    }
  };

  const loading = weatherLoading || closuresLoading;

  // Compute optimality label for the selected table
  const proposalScore = indoorProposal?.selectedTable
    ? getOptimalityScore(indoorProposal.selectedTable.capacity, indoorProposal.reservation.numberOfPeople)
    : null;

  // Count open dates in selection for bulk
  const selectedOpenCount = [...selectedDates].filter(d => getClosureStatus(d) !== 'closed').length;
  const selectedClosedCount = [...selectedDates].filter(d => { const s = getClosureStatus(d); return s === 'closed' || s === 'partial'; }).length;

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
        <div>
          <h2 className="text-lg font-semibold">Gestion terrasse</h2>
          <p className="text-sm text-muted-foreground">Fenêtre : {maxBookingDays} jours</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap self-start sm:self-auto">
          <Button
            variant={bulkMode ? "default" : "outline"}
            size="sm"
            onClick={toggleBulkMode}
          >
            <CheckSquare className="w-4 h-4 mr-1" />
            {bulkMode ? "Annuler sélection" : "Sélection multiple"}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPeriodDialogOpen(true)}
          >
            <CalendarRange className="w-4 h-4 mr-1" />
            Fermer une période
          </Button>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="outline" size="sm" onClick={() => refetchWeather()} disabled={weatherLoading}>
                  <RefreshCw className={`w-4 h-4 mr-1 ${weatherLoading ? 'animate-spin' : ''}`} />
                  <span className="hidden sm:inline">Actualiser météo</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent>Rafraîchir les prévisions météo</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>

      <div className="space-y-2">
        {loading && dateRange.slice(0, 5).map(d => (
          <Skeleton key={d} className="h-16 w-full rounded-lg" />
        ))}

        {!loading && dateRange.map((dateStr) => {
          const weather = weatherByDate.get(dateStr);
          const status = getClosureStatus(dateStr);
          const terraceRes = terraceReservationsByDate.get(dateStr) || [];
          const isExpanded = expandedDate === dateStr;
          const dateClosures = getClosuresForDate(dateStr);
          const dateObj = new Date(dateStr);
          const isSelected = selectedDates.has(dateStr);

          return (
            <Card key={dateStr} className={cn("p-3", bulkMode && isSelected && "ring-2 ring-primary bg-primary/5")}>
              <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-3">
                {/* Row 1 mobile: Checkbox + Date + Weather + Status */}
                <div className="flex items-center gap-3 min-w-0">
                  {/* Bulk checkbox */}
                  {bulkMode && (
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => toggleDateSelection(dateStr)}
                      className="shrink-0"
                    />
                  )}

                  {/* Date */}
                  <div className="shrink-0">
                    <div className="text-sm font-medium capitalize whitespace-nowrap">
                      {format(dateObj, 'EEE d MMM', { locale: fr })}
                    </div>
                  </div>

                  {/* Weather icon + temp */}
                  <div className="flex items-center gap-1.5 shrink-0">
                    {weather ? (
                      <>
                        {getWeatherIcon(weather.category)}
                        <span className="text-sm">
                          {weather.tempMax !== null ? `${Math.round(weather.tempMax)}°C` : '—'}
                        </span>
                      </>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </div>

                  {/* Status badge */}
                  <div className="flex items-center gap-2">
                    {status === 'open' && (
                      <Badge variant="success" className="text-xs">Ouverte</Badge>
                    )}
                    {status === 'closed' && (
                      <Badge variant="destructive" className="text-xs">Fermée</Badge>
                    )}
                    {status === 'partial' && (
                      <Badge variant="warning" className="text-xs">Partielle</Badge>
                    )}
                    {terraceRes.length > 0 && (
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        <Users className="w-3 h-3" />
                        {terraceRes.length}
                      </span>
                    )}
                  </div>
                </div>

                {/* Row 2 mobile / inline desktop: Weather warning + Actions */}
                <div className="flex items-center gap-2 md:flex-1 justify-between md:justify-end">
                  {/* Weather warning */}
                  {weather?.isBadWeather && (
                    <span className="text-[11px] text-orange-600 dark:text-orange-400 flex items-center gap-1 truncate min-w-0">
                      <AlertTriangle className="w-3 h-3 shrink-0" />
                      <span className="truncate">{weather.suggestion}</span>
                    </span>
                  )}

                  {/* Actions */}
                  {!bulkMode && (
                    <div className="flex items-center gap-1.5 shrink-0">
                      {status === 'open' ? (
                        <>
                          {/* Primary: close whole day in one click */}
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-xs"
                            onClick={() => handleCloseTerrace(dateStr, null)}
                          >
                            Fermer
                          </Button>
                          {/* Secondary: slot-specific closure */}
                          {timeSlots.length > 0 && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                  <Clock className="w-3.5 h-3.5" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem className="text-xs text-muted-foreground" disabled>
                                  Fermer un créneau
                                </DropdownMenuItem>
                                {timeSlots.map(ts => (
                                  <DropdownMenuItem key={ts.id} onClick={() => handleCloseTerrace(dateStr, ts.id)}>
                                    {ts.start} - {ts.end}
                                  </DropdownMenuItem>
                                ))}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </>
                      ) : status === 'partial' ? (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-xs"
                            onClick={() => handleCloseTerrace(dateStr, null)}
                          >
                            Tout fermer
                          </Button>
                        </>
                      ) : (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button variant="outline" size="sm" className="text-xs" onClick={() => handleOpenTerrace(dateStr)}>
                                Rouvrir la journée
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Rouvrir la terrasse pour cette journée</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}

                      {terraceRes.length > 0 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0"
                          onClick={() => setExpandedDate(isExpanded ? null : dateStr)}
                        >
                          {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Expanded: show terrace reservations */}
              {isExpanded && terraceRes.length > 0 && (
                <div className="mt-3 pt-3 border-t space-y-2">
                  <p className="text-xs font-medium text-muted-foreground mb-2">Réservations terrasse :</p>
                  {terraceRes.map(r => {
                    const isTerracePreference = r.terracePreference === 'terrace';
                    const isOnTerraceTable = (r.tableId && terraceTableIds.has(r.tableId)) || (r.combinedTableId && terraceCombinedTableIds.has(r.combinedTableId));
                    return (
                      <div key={r.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between text-sm pl-3 sm:pl-4 py-2 sm:py-1.5 rounded-lg border gap-2">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 min-w-0">
                          <span className="truncate">{r.customerFirstName} {r.customerLastName} — {r.startTime.substring(0, 5)} ({r.numberOfPeople} pers.)</span>
                          <div className="flex items-center gap-1 flex-wrap">
                            {isTerracePreference && (
                              <Badge variant="outline" className="text-[10px]">Préférence terrasse</Badge>
                            )}
                            {isOnTerraceTable && !isTerracePreference && (
                              <Badge variant="secondary" className="text-[10px]">Table terrasse</Badge>
                            )}
                            {status === 'closed' && (
                              <Badge variant="destructive" className="text-[10px]">Impactée</Badge>
                            )}
                          </div>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-xs shrink-0 w-full sm:w-auto"
                          onClick={() => handleIndoorClick(r)}
                        >
                          <ArrowRightLeft className="w-3 h-3 mr-1" />
                          Intérieur
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Partial closures detail — interactive per-slot reopen */}
              {status === 'partial' && dateClosures.length > 0 && (
                <div className="mt-2 pt-2 border-t space-y-2">
                  <p className="text-xs text-muted-foreground font-medium">Créneaux fermés :</p>
                  <div className="flex flex-wrap gap-1.5">
                    {dateClosures.map(c => {
                      const ts = timeSlots.find(t => t.id === c.timeSlotId);
                      const label = ts ? `${ts.start} - ${ts.end}` : 'Inconnu';
                      return (
                        <Badge key={c.id} variant="secondary" className="gap-1 pr-1 cursor-default">
                          {label}
                          <button
                            className="ml-0.5 rounded-full p-0.5 hover:bg-muted-foreground/20 transition-colors"
                            onClick={() => handleRemoveSingleClosure(c.id, dateStr, c.timeSlotId)}
                            title={`Rouvrir ${label}`}
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </Badge>
                      );
                    })}
                  </div>
                  {dateClosures.length >= 2 && (
                    <Button
                      variant="link"
                      size="sm"
                      className="text-xs h-auto p-0"
                      onClick={() => handleOpenTerrace(dateStr)}
                    >
                      Tout rouvrir
                    </Button>
                  )}
                </div>
              )}
            </Card>
          );
        })}
      </div>

      {/* Bulk mode sticky bottom bar */}
      {bulkMode && (
        <div className="fixed bottom-0 left-0 right-0 z-40 bg-background border-t shadow-lg p-3 flex items-center justify-between gap-3">
          <div className="text-sm text-muted-foreground">
            <span className="font-medium text-foreground">{selectedDates.size}</span> jour{selectedDates.size !== 1 ? 's' : ''} sélectionné{selectedDates.size !== 1 ? 's' : ''}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={toggleBulkMode}
              disabled={bulkClosing || bulkOpening}
            >
              <X className="w-4 h-4 mr-1" />
              Annuler
            </Button>
            {selectedClosedCount > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleBulkOpen}
                disabled={bulkOpening || bulkClosing}
              >
                {bulkOpening ? 'Réouverture…' : `Rouvrir la sélection (${selectedClosedCount})`}
              </Button>
            )}
            {selectedOpenCount > 0 && (
              <Button
                size="sm"
                onClick={handleBulkClose}
                disabled={bulkClosing || bulkOpening}
              >
                {bulkClosing ? 'Fermeture…' : `Fermer la sélection (${selectedOpenCount})`}
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Period closure dialog */}
      <Dialog open={periodDialogOpen} onOpenChange={(open) => { if (!open) { setPeriodDialogOpen(false); setPeriodStart(undefined); setPeriodEnd(undefined); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Fermer une période</DialogTitle>
            <DialogDescription>
              Sélectionnez les dates de début et de fin pour fermer la terrasse sur toute la période.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <label className="text-sm font-medium">Date de début</label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className={cn("w-full justify-start text-left font-normal", !periodStart && "text-muted-foreground")}>
                    <CalendarRange className="w-4 h-4 mr-2" />
                    {periodStart ? format(periodStart, 'd MMMM yyyy', { locale: fr }) : "Sélectionner…"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={periodStart}
                    onSelect={setPeriodStart}
                    disabled={(date) => date < startOfDay(new Date())}
                    initialFocus
                    className={cn("p-3 pointer-events-auto")}
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Date de fin</label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className={cn("w-full justify-start text-left font-normal", !periodEnd && "text-muted-foreground")}>
                    <CalendarRange className="w-4 h-4 mr-2" />
                    {periodEnd ? format(periodEnd, 'd MMMM yyyy', { locale: fr }) : "Sélectionner…"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={periodEnd}
                    onSelect={setPeriodEnd}
                    disabled={(date) => date < (periodStart || startOfDay(new Date()))}
                    initialFocus
                    className={cn("p-3 pointer-events-auto")}
                  />
                </PopoverContent>
              </Popover>
            </div>

            {periodStart && periodEnd && (
              <div className="text-sm text-muted-foreground bg-muted/50 rounded-lg p-3">
                {(() => {
                  const days = eachDayOfInterval({ start: periodStart, end: periodEnd });
                  return `${days.length} jour${days.length > 1 ? 's' : ''} seront fermés`;
                })()}
              </div>
            )}
          </div>

          <DialogFooter className="flex flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => { setPeriodDialogOpen(false); setPeriodStart(undefined); setPeriodEnd(undefined); }} disabled={periodClosing} className="w-full sm:w-auto">
              Annuler
            </Button>
            <Button onClick={handlePeriodClose} disabled={periodClosing || !periodStart || !periodEnd} className="w-full sm:w-auto">
              {periodClosing ? 'Fermeture…' : 'Fermer la période'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Impact dialog when closing terrace with existing reservations */}
      {closingDate && (
        <TerraceClosureImpactDialog
          open={impactDialogOpen}
          onOpenChange={setImpactDialogOpen}
          date={closingDate}
          timeSlotId={closingTimeSlotId}
          reservations={terraceReservationsByDate.get(closingDate) || []}
          establishmentId={establishmentId}
          tables={tables}
          combinedTables={combinedTables}
          allReservations={reservations}
          availabilities={availabilities}
          weekdayAvailabilities={weekdayAvailabilities}
          bookablePeriods={bookablePeriods}
          childrenCapacityMode={childrenCapacityMode}
          durationMinutes={durationMinutes}
          customAssignmentOrderEnabled={settings?.customAssignmentOrderEnabled ?? false}
          onConfirm={() => {
            confirmClosure(closingDate, closingTimeSlotId);
            setImpactDialogOpen(false);
            setClosingDate(null);
          }}
        />
      )}

      {/* Indoor reassignment confirmation dialog */}
      <Dialog open={!!indoorProposal} onOpenChange={(open) => { if (!open) setIndoorProposal(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Réassigner en intérieur</DialogTitle>
            <DialogDescription>
              {indoorProposal?.reservation.customerFirstName} {indoorProposal?.reservation.customerLastName} — {indoorProposal?.reservation.numberOfPeople} pers. à {indoorProposal?.reservation.startTime.substring(0, 5)}
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 space-y-3">
            {indoorProposal?.selectedTable ? (
              <div className="flex items-center gap-3 p-4 rounded-lg border bg-muted/30">
                <div className="flex-1">
                  <p className="text-sm font-medium">
                    {indoorProposal.selectedTable.id === indoorProposal.proposedTable?.id ? 'Table recommandée' : 'Table sélectionnée'}
                  </p>
                  <p className="text-lg font-bold">{indoorProposal.selectedTable.name}</p>
                  <p className="text-sm text-muted-foreground">
                    {indoorProposal.selectedTable.capacity} place{indoorProposal.selectedTable.capacity > 1 ? 's' : ''}
                    {indoorProposal.selectedTable.type === 'combined' && ' (combinée)'}
                  </p>
                </div>
                {proposalScore && (
                  <Badge className={`${proposalScore.badgeClass} text-white`}>
                    {proposalScore.label}
                  </Badge>
                )}
              </div>
            ) : (
              <div className="p-4 rounded-lg border border-destructive/50 bg-destructive/10">
                <div className="flex items-center gap-2 text-destructive">
                  <AlertTriangle className="w-4 h-4" />
                  <p className="text-sm font-medium">Aucune table intérieure disponible</p>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Vous pouvez quand même changer la préférence en intérieur sans réassigner de table.
                </p>
              </div>
            )}

            {/* Table alternative selector */}
            {indoorProposal && indoorProposal.allAvailableTables.length > 1 && (
              <div className="space-y-1.5">
                <p className="text-xs font-medium text-muted-foreground">Choisir une autre table :</p>
                <div className="max-h-40 overflow-y-auto space-y-1 rounded-lg border p-2">
                  {indoorProposal.allAvailableTables.map(t => {
                    const isSelected = indoorProposal.selectedTable?.id === t.id;
                    const isRecommended = indoorProposal.proposedTable?.id === t.id;
                    const score = getOptimalityScore(t.capacity, indoorProposal.reservation.numberOfPeople);
                    return (
                      <button
                        key={t.id}
                        onClick={() => setIndoorProposal(prev => prev ? { ...prev, selectedTable: t } : null)}
                        className={cn(
                          "w-full flex items-center justify-between px-3 py-2 rounded-md text-sm transition-colors text-left",
                          isSelected ? "bg-primary/10 border border-primary" : "hover:bg-muted"
                        )}
                      >
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{t.name}</span>
                          <span className="text-muted-foreground text-xs">
                            {t.capacity} pl.{t.type === 'combined' ? ' (comb.)' : ''}
                          </span>
                          {isRecommended && (
                            <Badge variant="outline" className="text-[10px] px-1.5 py-0">Recommandée</Badge>
                          )}
                        </div>
                        <Badge className={`${score.badgeClass} text-white text-[10px] px-1.5 py-0`}>
                          {score.label}
                        </Badge>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="flex flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setIndoorProposal(null)} disabled={reassigning} className="w-full sm:w-auto">
              Annuler
            </Button>
            {indoorProposal?.selectedTable ? (
              <Button onClick={handleConfirmIndoor} disabled={reassigning} className="w-full sm:w-auto">
                Confirmer le déplacement
              </Button>
            ) : (
              <>
                <Button variant="secondary" onClick={handlePreferenceOnlyIndoor} disabled={reassigning} className="w-full sm:w-auto">
                  Changer la préférence
                </Button>
                <Button variant="destructive" onClick={handleCancelAndNotify} disabled={reassigning} className="w-full sm:w-auto">
                  Annuler et notifier
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email preview dialog */}
      <Dialog open={!!emailAction} onOpenChange={(open) => { if (!open) setEmailAction(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {emailAction?.type === 'weather_confirmation' ? '📧 Envoyer un email de confirmation' : '📧 Envoyer un email d\'annulation'}
            </DialogTitle>
            <DialogDescription>
              {emailAction?.reservation.customerFirstName} {emailAction?.reservation.customerLastName} — {emailAction?.reservation.customerEmail || 'Pas d\'email'}
            </DialogDescription>
          </DialogHeader>

          {emailAction && !emailAction.reservation.customerEmail ? (
            <div className="p-4 rounded-lg border border-destructive/50 bg-destructive/10">
              <div className="flex items-center gap-2 text-destructive">
                <AlertTriangle className="w-4 h-4" />
                <p className="text-sm font-medium">Ce client n'a pas d'adresse email</p>
              </div>
            </div>
          ) : emailAction && (
            <div className="space-y-3">
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1">Aperçu de l'email :</p>
                <ScrollArea className="h-64 rounded-lg border bg-background">
                  <div
                    className="p-4"
                    dangerouslySetInnerHTML={{ __html: generateEmailPreviewHtml(emailAction) }}
                  />
                </ScrollArea>
                <p className="text-[10px] text-muted-foreground mt-1">
                  L'email envoyé correspondra exactement à l'aperçu ci-dessus.
                </p>
              </div>
            </div>
          )}

          <DialogFooter className="flex flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setEmailAction(null)} disabled={sendingEmail} className="w-full sm:w-auto">
              Ne pas envoyer
            </Button>
            {emailAction?.reservation.customerEmail && (
              <Button onClick={handleSendEmail} disabled={sendingEmail} className="w-full sm:w-auto">
                {sendingEmail ? 'Envoi…' : 'Envoyer l\'email'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
